import React, { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { useSitesWithGoods } from '../../../hooks/useSitesWithGoods';
import { RootState } from '../../../redux/reducers';
import {
    clearWaybill,
    createNewEmptyWaybill,
    importWaybill,
    saveWaybill,
    updateWaybillDescription,
    updateWaybillPickupDate,
    updateWaybillDeliveryDate,
    updateWaybillDriverName,
    updateWaybillDriverUniqueNumber,
    updateWaybillLicensePlateNum,
} from '../../../redux/actions/waybillActions';
import { Typography, DialogContent, Grid2, TextField } from '@mui/material';
import { CompleteOrderType } from '../../../common/Types';
import { SaveEntityStatus } from '../../../redux/reducers/common';
import { getClientNameInfo, getSiteOrdersTableData } from '../OrdersHelper';
import { LocalizationProvider, DateTimePicker, renderTimeViewClock } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/sr';
import { filterUniqueBareGoodsByArticle } from '../../../redux/actions/helper';
import { setBareGoodsEntities, importOrder, clearOrder, clearBareGoods } from '../../../redux/actions/orderActions';
import OrderFormGoods from './OrderFormGoods';
import { convertOrderGoodsItemsToBareGoodsEntities } from './OrderFormHelper';
import { importApprovedOrderGoodsItems } from '../../../redux/actions/orderGoodsItemsActions';

import { FormActions, FormDialogTitle } from '../../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../../common/components/mobile/MobilePrimitives';

type WaybillFormProps = {
    closeForm(): void;
    open: boolean;
    waybillId?: number;
    orderId?: number;
};

export default function WaybillForm(props: WaybillFormProps): ReactElement {
    const { open, closeForm, waybillId, orderId } = props;

    const order = useAppSelector((state: RootState) => state.orderForm.order);

    const waybill = useAppSelector((state: RootState) => state.waybillForm.waybill);

    const saveStatus = useAppSelector((state: RootState) => state.waybillForm.saveStatus);

    const approvedOrderGoodsItems = useAppSelector((state: RootState) => state.orderGoodsItems.approvedOrderGoodsItems);
    const goods = useAppSelector((state: RootState) => state.goods.goods);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (saveStatus === SaveEntityStatus.Saved) {
            handleClose();
        }
    }, [dispatch, saveStatus]);

    useEffect(() => {
        if (!!order?.id && !!order.orderGoodsItems && order.orderGoodsItems.length > 0 && !!approvedOrderGoodsItems) {
            const site = sites?.find((s) => s.id === order.originSiteId);

            const orderedOrderGoodsItems = approvedOrderGoodsItems?.filter(
                (ogi) => !!ogi.order && ogi.order.originSiteId === order.originSiteId,
            );
            const existingBareGoods = convertOrderGoodsItemsToBareGoodsEntities(
                order.orderGoodsItems,
                site,
                orderedOrderGoodsItems,
            );

            const filteredGoods = filterUniqueBareGoodsByArticle(existingBareGoods);

            dispatch(setBareGoodsEntities(filteredGoods));
        }
    }, [order, approvedOrderGoodsItems]);

    useEffect(() => {
        if (open && !approvedOrderGoodsItems) {
            dispatch(importApprovedOrderGoodsItems());
        }
    }, [dispatch, open]);

    useEffect(() => {
        if (!!orderId && open && !order) {
            dispatch(importOrder(orderId));
        }
    }, [dispatch, open, orderId]);

    useEffect(() => {
        if (open) {
            if (!!waybillId && waybillId > 0) {
                dispatch(importWaybill(waybillId));
            } else {
                if (!waybill) {
                    dispatch(createNewEmptyWaybill());
                }
            }
        }
    }, [dispatch, waybillId, open]);

    const sites = useSitesWithGoods();
    const clients = useSelector((state: RootState) => state.clients.clients);

    const [originInfo, setOriginInfo] = useState<string>('/');
    const [destinationInfo, setDestinationInfo] = useState<string>('/');

    useEffect(() => {
        if (!!order) {
            const destinationSite = order.destinationSite;
            const originSite = order.originSite;
            const supplier = order.supplier;

            const destinationData = !destinationSite
                ? '/'
                : getSiteOrdersTableData(destinationSite, sites ?? undefined, clients ?? undefined);

            const originData = !originSite
                ? !supplier
                    ? '/'
                    : (getClientNameInfo(supplier) ?? '/')
                : getSiteOrdersTableData(originSite, sites ?? undefined, clients ?? undefined);

            setOriginInfo(originData);
            setDestinationInfo(destinationData);
        }
    }, [order]);

    const handlePickupDateChange = (date: Dayjs | null) => {
        dispatch(updateWaybillPickupDate(date ? date.toDate() : undefined));
    };

    const handleDeliveryDateChange = (date: Dayjs | null) => {
        dispatch(updateWaybillDeliveryDate(date ? date.toDate() : undefined));
    };

    const handleDescriptionChange = (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(updateWaybillDescription(e.target.value));
    };

    const handleDriverNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(updateWaybillDriverName(e.target.value));
    };

    const handleDriverUniqueNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(updateWaybillDriverUniqueNumber(e.target.value));
    };

    const handleLicensePlateNumChange = (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(updateWaybillLicensePlateNum(e.target.value));
    };

    const handleSave = () => {
        dispatch(saveWaybill(orderId ?? 0));
    };

    const handleClose = () => {
        dispatch(clearOrder());
        dispatch(clearBareGoods());
        dispatch(clearWaybill());
        closeForm();
    };

    const isRequiredEmpty =
        !waybill?.driverName ||
        waybill.driverName.trim() === '' ||
        !waybill?.driverUniqueNumber ||
        waybill.driverUniqueNumber.trim() === '' ||
        !waybill?.vehicleLicensePlateNum ||
        waybill.vehicleLicensePlateNum.trim() === '';

    return (
        <ResponsiveDialog scroll="body" fullScreen open={open} onClose={handleClose}>
            <FormDialogTitle onClose={handleClose}>Otpremnica</FormDialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 5 }, pt: { xs: 3, sm: 5 } }}>
                <Grid2
                    container
                    spacing={2}
                    sx={{ maxWidth: '800px', minWidth: { xs: 200, md: '600px' }, margin: 'auto' }}
                >
                    <Grid2 size={{ xs: 12 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Br. dokumenta: <span style={{ fontWeight: 'bold' }}>{waybill?.waybillNumber ?? '/'}</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sr">
                            <DateTimePicker
                                label="Vreme preuzimanja"
                                viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                    seconds: renderTimeViewClock,
                                }}
                                value={waybill?.pickupDate ? dayjs(waybill?.pickupDate) : null}
                                onChange={handlePickupDateChange}
                            />
                        </LocalizationProvider>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sr">
                            <DateTimePicker
                                label="Vreme dostave"
                                viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                    seconds: renderTimeViewClock,
                                }}
                                value={waybill?.deliveryDate ? dayjs(waybill?.deliveryDate) : null}
                                onChange={handleDeliveryDateChange}
                            />
                        </LocalizationProvider>
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Vrsta naloga: <span style={{ fontWeight: 'bold' }}>Nalog za otpremanje robe</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 6 }} />
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Izvor: <span style={{ fontWeight: 'bold' }}>{originInfo ?? '/'}</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Odredište: <span style={{ fontWeight: 'bold' }}>{destinationInfo ?? '/'}</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />

                    <Grid2 size={{ xs: 12 }}>
                        <Typography
                            variant="body1"
                            component="h6"
                            style={{ fontSize: 20, borderBottom: '1px solid rgba(155,155,155,1)' }}
                        >
                            Roba
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <OrderFormGoods
                            completeOrderType={CompleteOrderType.Transfer}
                            isOriginChosen={!!order?.originSiteId && order.originSiteId > 0}
                            isReceipt
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            required
                            error={!waybill?.driverName || waybill.driverName.trim() === ''}
                            fullWidth
                            name="driverName"
                            label="Vozač"
                            value={waybill?.driverName ?? ''}
                            variant="outlined"
                            onChange={handleDriverNameChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            required
                            error={!waybill?.driverUniqueNumber || waybill.driverUniqueNumber.trim() === ''}
                            fullWidth
                            name="driverUniqueNumber"
                            label="JMBG"
                            value={waybill?.driverUniqueNumber ?? ''}
                            variant="outlined"
                            onChange={handleDriverUniqueNumberChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            required
                            error={!waybill?.vehicleLicensePlateNum || waybill.vehicleLicensePlateNum.trim() === ''}
                            fullWidth
                            name="vehicleLicensePlateNum"
                            label="Br. tablica"
                            value={waybill?.vehicleLicensePlateNum ?? ''}
                            variant="outlined"
                            onChange={handleLicensePlateNumChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />
                    <Grid2 size={{ xs: 12 }}>
                        <TextField
                            required={false}
                            fullWidth
                            name="description"
                            label="Napomena"
                            multiline
                            rows={6}
                            value={waybill?.description ?? ''}
                            variant="outlined"
                            onChange={handleDescriptionChange}
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />
                    <FormActions
                        onCancel={handleClose}
                        onSubmit={handleSave}
                        submitLabel="Sačuvaj"
                        disabled={saveStatus === SaveEntityStatus.Pending || isRequiredEmpty}
                        pending={saveStatus === SaveEntityStatus.Pending}
                    />
                </Grid2>
            </DialogContent>
        </ResponsiveDialog>
    );
}
